import { createFileRoute } from '@tanstack/react-router';
import { useFilters } from '@/hooks/useFilters';
import { PatientsList } from '@/components/patients/patientsList';
import { PatientsFilters } from '@/components/patients/patientsFilters';
import { useTranslation } from 'react-i18next';
import { AddPatient } from '@/components/patients/addPatient';
import { usePermission } from '@/hooks/usePermission';
import { useUser } from '@/api/queries/users';
import { useContext, useEffect, useState } from 'react';
import { PatientsContext, DEFAULT_FILTERS } from '@/context/patientsContext';
import { PatientFilters } from '@/types/patients/filters';

export const Route = createFileRoute('/_app/patients/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const canAddPatients = usePermission('patient.canEdit', {
    allowAnySite: true,
  }).hasPermissions;
  const hasReadPermission = usePermission('patient.canRead').hasPermissions;
  const { data: user } = useUser();

  const { page, setPage, setActiveFilters } = useContext(PatientsContext);
  const resetPage = () => {
    void setPage(1);
  };
  const [ siteId, setSiteId ] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!user) return;

    setSiteId(user.site.id);
  }, [ user ]);

  const filters = useFilters(
    {
      ...DEFAULT_FILTERS,
      siteId: hasReadPermission ? null : siteId,
    },
    { onFiltersChanged: resetPage },
  );

  useEffect(() => {
    setActiveFilters(filters.serializedFilters as PatientFilters);
  }, [ filters.serializedFilters ]);

  return (
    <div className="flex flex-col flex-1">
      <div className="flex justify-between items-center mb-space-2xl">
        <h1 className="text-3xl font-semibold">
          { t('title', { ns: 'patients' }) }
        </h1>
        { canAddPatients && <AddPatient /> }
      </div>
      <div className="flex flex-col gap-6">
        <PatientsFilters filters={ filters } />
        <PatientsList
          activeFilters={ filters.serializedFilters }
          page={ page }
          setPage={ page => void setPage(page) }
        />
      </div>
    </div>
  );
}
